import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, Mail, LogIn } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "../context/AuthContext";
import { Toaster, toast } from "sonner";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/admin/dashboard");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Login failed";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0E0E0E] text-white flex items-center justify-center px-6 py-12" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="flex items-center gap-3 mb-10">
            <img src="/logos/gm-logo-dark.png" alt="GM Print" width={48} height={48} />
            <div>
              <div className="font-display text-xl leading-none">GM PRINT</div>
              <div className="font-mono text-[10px] tracking-widest text-white/50 mt-0.5">ADMIN</div>
            </div>
          </div>

          <h1 className="font-display text-5xl md:text-6xl mb-3">Sign in.</h1>
          <p className="font-body text-white/60 mb-10">Access analytics, enquiries, and settings.</p>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
            <div>
              <label className="label-caps block mb-2 text-white/70">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="admin@gmprint.com.au"
                  data-testid="admin-email-input"
                  className="w-full pl-11 pr-4 py-3 bg-transparent border-2 border-white/20 text-white placeholder:text-white/30 focus:border-[#00AEEF] outline-none transition-colors"
                />
              </div>
            </div>

            <div>
              <label className="label-caps block mb-2 text-white/70">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/40" />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  data-testid="admin-password-input"
                  className="w-full pl-11 pr-4 py-3 bg-transparent border-2 border-white/20 text-white placeholder:text-white/30 focus:border-[#00AEEF] outline-none transition-colors"
                />
              </div>
            </div>

            {error && (
              <div className="text-sm text-[#EC008C] font-mono py-2" data-testid="admin-login-error">{error}</div>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-submit"
              className="w-full flex items-center justify-center gap-3 bg-white text-black py-4 font-body font-semibold text-sm uppercase tracking-[0.15em] hover:bg-[#00AEEF] transition-colors disabled:opacity-50"
            >
              {loading ? "Signing in..." : (<>Sign in <LogIn size={16} /></>)}
            </button>
          </form>

          <div className="cmyk-bar cmyk-bar-thin mt-12">
            <div className="c" /><div className="m" /><div className="y" /><div style={{background:'#fff'}} />
          </div>
        </motion.div>
      </div>
      <Toaster position="bottom-right" richColors theme="dark" />
    </div>
  );
}
